import openai from './openaiClient';

/**
 * Generates a chat completion response based on user input using GPT-5.
 * @param {string} userMessage - The user's input message.
 * @param {string} systemMessage - Optional system message for context.
 * @returns {Promise<string>} The assistant's response.
 */
export async function getBasicChatCompletion(userMessage, systemMessage = 'You are a helpful assistant for Clean360 cleaning services. Provide professional, friendly advice about cleaning services, scheduling, and general inquiries.') {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { role: 'system', content: systemMessage },
        { role: 'user', content: userMessage },
      ],
      max_completion_tokens: 2000,
      reasoning_effort: 'medium',
      verbosity: 'medium'
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    console.error('Error in basic chat completion:', error);
    throw error;
  }
}

/**
 * Streams a chat completion response chunk by chunk for real-time interaction.
 * @param {string} userMessage - The user's input message.
 * @param {Function} onChunk - Callback to handle each streamed chunk.
 * @param {string} systemMessage - Optional system message for context.
 */
export async function getStreamingChatCompletion(userMessage, onChunk, systemMessage = 'You are a helpful assistant for Clean360 cleaning services.') {
  try {
    const stream = await openai?.chat?.completions?.create({
      model: 'gpt-5-mini', // Fast model for streaming
      messages: [
        { role: 'system', content: systemMessage },
        { role: 'user', content: userMessage },
      ],
      stream: true,
      reasoning_effort: 'minimal', // For faster streaming
    });

    for await (const chunk of stream) {
      const content = chunk?.choices?.[0]?.delta?.content || '';
      if (content) {
        onChunk(content);
      }
    }
  } catch (error) {
    console.error('Error in streaming chat completion:', error);
    throw error;
  }
}

/**
 * Generates structured response for specific use cases like service recommendations.
 * @param {string} userMessage - The user's input message.
 * @param {Object} customSchema - Custom JSON schema for the response.
 * @returns {Promise<Object>} Structured response object.
 */
export async function getStructuredResponse(userMessage, customSchema = null) {
  const defaultSchema = {
    type: 'object',
    properties: {
      service_type: { type: 'string', description: 'Recommended cleaning service type' },
      recommendations: { 
        type: 'array', 
        items: { type: 'string' },
        description: 'List of specific recommendations'
      },
      estimated_cost: { type: 'string', description: 'Estimated cost range' },
      next_steps: { 
        type: 'array',
        items: { type: 'string' },
        description: 'Recommended next steps for the customer'
      }
    },
    required: ['service_type', 'recommendations'],
    additionalProperties: false,
  };

  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: 'You are a professional cleaning service consultant. Analyze customer needs and provide structured recommendations for Clean360 services.' 
        },
        { role: 'user', content: userMessage },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'service_recommendation',
          schema: customSchema || defaultSchema,
        },
      },
      reasoning_effort: 'medium',
      verbosity: 'medium',
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error in structured response:', error);
    throw error;
  }
}

/**
 * Analyzes customer feedback or reviews using AI.
 * @param {string} feedbackText - The customer feedback text.
 * @returns {Promise<Object>} Analysis results.
 */
export async function analyzeFeedback(feedbackText) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: 'You are a customer feedback analyst for a cleaning service company. Analyze the sentiment, key issues, and provide actionable insights.' 
        },
        { role: 'user', content: `Analyze this customer feedback: "${feedbackText}"` },
      ],
      response_format: {
        type: 'json_schema',
        json_schema: {
          name: 'feedback_analysis',
          schema: {
            type: 'object',
            properties: {
              sentiment: { type: 'string', enum: ['positive', 'negative', 'neutral'] },
              key_issues: { type: 'array', items: { type: 'string' } },
              satisfaction_score: { type: 'number', minimum: 1, maximum: 10 },
              actionable_insights: { type: 'array', items: { type: 'string' } },
              response_priority: { type: 'string', enum: ['high', 'medium', 'low'] }
            },
            required: ['sentiment', 'satisfaction_score'],
            additionalProperties: false,
          },
        },
      },
      reasoning_effort: 'high',
      verbosity: 'high',
    });

    return JSON.parse(response?.choices?.[0]?.message?.content);
  } catch (error) {
    console.error('Error in feedback analysis:', error);
    throw error;
  }
}

/**
 * Generates cleaning tips and advice.
 * @param {string} query - The user's cleaning question.
 * @returns {Promise<string>} Professional cleaning advice.
 */
export async function getCleaningAdvice(query) {
  try {
    const response = await openai?.chat?.completions?.create({
      model: 'gpt-5',
      messages: [
        { 
          role: 'system', 
          content: 'You are a professional cleaning expert with years of experience. Provide detailed, practical cleaning advice that is safe and effective. Always prioritize safety and mention any necessary precautions.' 
        },
        { role: 'user', content: query },
      ],
      max_completion_tokens: 1500,
      reasoning_effort: 'medium',
      verbosity: 'high'
    });

    return response?.choices?.[0]?.message?.content;
  } catch (error) {
    console.error('Error getting cleaning advice:', error);
    throw error;
  }
}

/**
 * Moderates user-generated content for safety.
 * @param {string} text - The text to moderate.
 * @returns {Promise<object>} Moderation results.
 */
export async function moderateContent(text) {
  try {
    const response = await openai?.moderations?.create({
      model: 'text-moderation-latest',
      input: text,
    });

    return response?.results?.[0];
  } catch (error) {
    console.error('Error moderating content:', error);
    throw error;
  }
}